import { Checkbox, SwitchField } from 'components/widgets';
import WizardFooter from 'components/widgets/WizardFooter';
import Wizardable from 'components/widgets/Wizardable';
import PropTypes from 'prop-types';
import React from 'react';
import { Translate } from 'react-localize-redux';
import { Tooltip as ReactTooltip } from 'react-tippy';
import Icon, { IconSizes, IconVariants } from '../../components/foundations/Icon';
import Label from '../../components/foundations/Label';
import Tooltip from '../../components/molecules/Tooltip';
import { SECTION_ACCESS_TYPES } from '../../utils/consts';
import AddFutureItems from '../containers/AddFutureItems';
import { VIEW_ONLY_PAGES_MAP, isScreenViewOnly } from '../sections';
import { SectionPermissionSchema } from '../types';
import AccessTypeIcon from './AccessTypeIcon';
import ExpandableGroup from './ExpandableGroup';
import css from './ScreenSelection.css';
import WizardWarningMessage from './WizardWarningMessage';

class ScreenSelection extends Wizardable {
    constructor(props) {
        super(props);
        this.state = {
            sourceFilter: '',
        };
        this.toggleAll = this.toggleAll.bind(this);
        this.buttonOverrides = props.wizardOverrideButtons;
        this.allScreens = this._getAllScreens(props.screens);
        this.disabledScreens = this.allScreens.filter(screen => screen.disabled);
    }

    _getAllScreens(screens) {
        return screens.reduce((total, section) => {
            total.push(...section.children);
            return total;
        }, []);
    }

    toggleAll() {
        const { toggleScreens, selectedScreens } = this.props;
        const filteredFlat = this.allScreens.filter(screen => this.disabledScreens.indexOf(screen) === -1);
        const isAllScreensSelected = selectedScreens.length === filteredFlat.length;
        toggleScreens(
            filteredFlat.map(screen => screen.name),
            !isAllScreensSelected
        );
    }

    sectionCheckboxStateChanged(section, newState) {
        this.toggleAllInSection(section, !!newState);
    }

    toggleAllInSection(section, selected) {
        const { toggleScreens, sectionPermissions } = this.props;
        toggleScreens(
            section.children.map(screen => screen.name),
            selected
        );
        // When deselecting all, reset view-only for any screens that had it enabled
        if (!selected) {
            section.children.forEach(screen => {
                if (isScreenViewOnly(screen.name, sectionPermissions)) {
                    this.toggleScreenViewOnly(screen.name, false);
                }
            });
        }
    }

    screenCheckboxStateChanged(screen, newState) {
        const { toggleScreens, sectionPermissions } = this.props;
        toggleScreens([screen.name], newState);
        // When deselecting a screen, turn off view-only if it was enabled
        if (!newState && isScreenViewOnly(screen.name, sectionPermissions)) {
            this.toggleScreenViewOnly(screen.name, false);
        }
    }

    showViewOnlyCheckbox(screenName) {
        return Object.keys(VIEW_ONLY_PAGES_MAP).includes(screenName);
    }

    toggleScreenViewOnly(screenName, newState) {
        const { updateSectionPermissions } = this.props;
        const sections = VIEW_ONLY_PAGES_MAP[screenName];
        if (!sections) return;
        const accessType = newState ? SECTION_ACCESS_TYPES.VIEW : SECTION_ACCESS_TYPES.EDIT;
        updateSectionPermissions(sections, accessType);
    }

    render() {
        const { screens, lastAction, translate, sectionPermissions } = this.props;
        return (
            <>
                <WizardWarningMessage show message="STATIC.PAGES.TEAM_MEMBERS.SCREEN_SELECTION_WARNING" />
                <this.AnimationItem initialPose={lastAction} pose="visible" className={css.page}>
                    <div className={css.headline}>
                        <div className={css.preamble}>
                            <Translate id="STATIC.PAGES.TEAM_MEMBERS.WIZARD_SCREEN_SELECTION_TITLE" />
                            <div className={css.faqLine}>
                                For more information{' '}
                                <a
                                    href="https://singular.zendesk.com/hc/en-us/articles/360020835952-Team-Management-FAQ"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    read the FAQ
                                </a>
                                .
                            </div>
                        </div>
                    </div>
                    <div className={css.contentArea}>
                        {screens.map(section => {
                            const selectedChildren = section.children.filter(child => child.selected);
                            const sectionChecked = selectedChildren.length === section.children.length;
                            const sectionIndeterminate =
                                selectedChildren.length > 0 && selectedChildren.length < section.children.length;
                            const hasWarnings = section.children.filter(child => child.warning === true).length > 0;
                            return (
                                <div className={css.expandableGroupHolder} key={section.name}>
                                    <ExpandableGroup
                                        marked={sectionIndeterminate || sectionChecked}
                                        hasWarning={hasWarnings}
                                        title={section.displayName}
                                        rightComponents={
                                            <Checkbox
                                                label={translate('STATIC.PAGES.TEAM_MEMBERS.SELECTED_COUNT', {
                                                    selectedCount: selectedChildren.length,
                                                    totalCount: section.children.length,
                                                })}
                                                className={css.sectionCheckbox}
                                                labelPosition="left"
                                                checked={sectionChecked}
                                                threeStatesEnabled
                                                indeterminate={sectionIndeterminate}
                                                onChange={newState =>
                                                    this.sectionCheckboxStateChanged(section, newState)
                                                }
                                            />
                                        }
                                    >
                                        <div>
                                            {section.children.map(screen => {
                                                if (screen.selected && screen.disabled) {
                                                    this.screenCheckboxStateChanged(screen, false);
                                                }
                                                return (
                                                    <div className={css.screenContainer} key={screen.name}>
                                                        {screen.warning ? (
                                                            <Icon
                                                                name="info-triangle"
                                                                size={IconSizes.X_SMALL}
                                                                variant={IconVariants.WARNING}
                                                                className={css.screenWarning}
                                                            />
                                                        ) : null}
                                                        <div className={css.screenRow}>
                                                            <ReactTooltip
                                                                key={`${screen.name}Feature`}
                                                                html={screen.disabledTooltip}
                                                                disabled={!screen.disabledTooltip}
                                                                position="top-start"
                                                                inertiaf
                                                                animation="scale"
                                                                distance={10}
                                                                theme="light"
                                                                size="big"
                                                            >
                                                                <Checkbox
                                                                    label={screen.displayName}
                                                                    checked={screen.selected}
                                                                    className={css.sectionCheckbox}
                                                                    onChange={newState =>
                                                                        this.screenCheckboxStateChanged(
                                                                            screen,
                                                                            newState
                                                                        )
                                                                    }
                                                                    size="small"
                                                                    checkmarkStyle={{ marginRight: 6 }}
                                                                    disabled={screen.disabled}
                                                                />
                                                            </ReactTooltip>
                                                            {this.showViewOnlyCheckbox(screen.name) && (
                                                                <Tooltip
                                                                    titleTranslationKey="STATIC.PAGES.TEAM_MEMBERS.MANAGE_PERMISSIONS_PAGE.VIEW_ONLY_TOOLTIP"
                                                                    wrapperClass={css.tooltipWrapper}
                                                                >
                                                                    <SwitchField
                                                                        checked={isScreenViewOnly(
                                                                            screen.name,
                                                                            sectionPermissions
                                                                        )}
                                                                        onChange={newState => {
                                                                            this.toggleScreenViewOnly(
                                                                                screen.name,
                                                                                newState
                                                                            );
                                                                            if (newState) {
                                                                                this.screenCheckboxStateChanged(
                                                                                    screen,
                                                                                    true
                                                                                );
                                                                            }
                                                                        }}
                                                                        type="small"
                                                                    />
                                                                    <AccessTypeIcon
                                                                        accessType={SECTION_ACCESS_TYPES.VIEW}
                                                                        size={IconSizes.X_SMALL}
                                                                        interactive={false}
                                                                        className={css.viewOnlyIcon}
                                                                        withTooltip={false}
                                                                    />
                                                                    <Label
                                                                        type="p"
                                                                        className={css.viewOnlyLabel}
                                                                        text={`STATIC.PAGES.TEAM_MEMBERS.MANAGE_PERMISSIONS_PAGE.SECTION_ACCESS_TYPES_TITLE.${SECTION_ACCESS_TYPES.VIEW}`}
                                                                    />
                                                                </Tooltip>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </ExpandableGroup>
                                </div>
                            );
                        })}
                    </div>
                </this.AnimationItem>
                <WizardFooter
                    buttons={this.getButtons()}
                    leftComponents={<AddFutureItems itemType="screens" form="screenSelectionForm" />}
                />
            </>
        );
    }
}

ScreenSelection.propTypes = {
    wizardOverrideButtons: PropTypes.objectOf(PropTypes.any),
    screens: PropTypes.arrayOf(PropTypes.any),
    sectionPermissions: PropTypes.arrayOf(SectionPermissionSchema),
    updateSectionPermissions: PropTypes.func,
    toggleScreens: PropTypes.func,
    translate: PropTypes.func,
    selectedScreens: PropTypes.arrayOf(PropTypes.string),
    lastAction: PropTypes.string,
};

ScreenSelection.defaultProps = {
    wizardOverrideButtons: {},
    screens: [],
    sectionPermissions: [],
    updateSectionPermissions: () => {},
    toggleScreens: () => {},
    translate: key => key,
    selectedScreens: [],
    lastAction: undefined,
};

export default ScreenSelection;
